/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';

import { StyledCard } from '#PRIVATE_StyledCard';

import { type ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { type TestIdProps } from '#hooks/UNSAFE_useTestId';
import { type AriaProps } from '#utils/UNSAFE_interpolations/aria';
import { type DimensionProps } from '#utils/UNSAFE_interpolations/dimensions';
import { type PaddingProps } from '#utils/UNSAFE_interpolations/padding';
import { useHover } from '#hooks/UNSAFE_useHover';

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;

type Props = StyleProps &
  TestIdProps & {
    children?: ComponentChildren;

    /**
     * This flag marks this card as selected within a collection.
     */
    isSelected?: boolean;
  };

/**
 * A selection card is a container element that is a part of a collection of elements, and renders a group of related information in a styled rectangular area.
 * Width, height, and backgroundColor are set through props.
 */
export const SelectionCard = ({ children, isSelected = false, testId, ...rest }: Props) => {
  const { hoverProps, isHover } = useHover();
  return (
    <StyledCard
      {...hoverProps}
      display="inlineFlex"
      isHover={isHover}
      isSelected={isSelected}
      parentComponentVariant="selectionCard"
      testId={testId}
      {...rest}>
      {children}
    </StyledCard>
  );
};
